import { css } from '@emotion/react'
import { Cell, FormControl, Grid, VFlow } from 'bold-ui'
import {
  CheckboxField,
  CnsField,
  CpfField,
  DateField,
  MunicipioSelectField,
  RadioGroupField,
  SexoSelectField,
  SimNaoRadioGroupField,
  TextField,
  usePecField,
} from 'components/form'
import { RecordStringSelectField } from 'components/form/core/RecordStringSelectField/RecordStringSelectField'
import { AldeiaSelectField } from 'components/form/field/select/AldeiaSelectField'
import { DseiSelectField } from 'components/form/field/select/DseiSelectField'
import { EtniaSelectField } from 'components/form/field/select/EtniaSelectField'
import { PaisSelectField } from 'components/form/field/select/PaisSelectField'
import { PoloBaseSelectField } from 'components/form/field/select/PoloBaseSelectField'
import { recordToSortedRadioOptions } from 'components/form/field/utils-field'
import { useServerTime } from 'hooks/useServerTime'
import { povoComunidadeTradicionalRecord } from 'view/cidadao/create/types/model-cidadao'

import { AlertEdicaoNaoAlteraCadastro } from '../AlertEdicaoNaoAlteraCadastro'
import {
  DadosIdentificacaoFormModel,
  gestanteSinanRecord,
  identidadeGeneroSinanRecord,
  metaNotificacaoAgravo,
  motivoAusenciaCpfSinanRecord,
  NOME_MAX_LENGTH,
  orientacaoSexualSinanRecord,
  racaCorSinanRecord,
} from '../model-notificacaoAgravo'

const meta = metaNotificacaoAgravo.dadosIdentificacao

export function DadosIdentificacaoStep() {
  const {
    input: { value },
  } = usePecField<DadosIdentificacaoFormModel>({
    name: meta.absolutePath(),
    subscription: { value: true },
  })

  const serverTime = useServerTime().getServerTimeNow()

  const naoPossuiCpf = !!value?.stNaoPossuiCpf
  const desconheceNomePai = !!value?.desconheceNomePai
  const desconheceNomeMae = !!value?.desconheceNomeMae
  const isIndigena = value?.racaCor === 'INDIGENA'
  const isAldeado = value?.isCidadaoAldeado === 'SIM'
  const isMembroComunidadeTradicional = value?.isMembroComunidadeTradicional === 'SIM'

  return (
    <VFlow>
      <AlertEdicaoNaoAlteraCadastro />

      <Grid>
        <Cell size={12}>
          <DateField name={meta.dataNotificacao} label='Data da notificação' maxDate={serverTime} required />
        </Cell>

        <Cell size={12}>
          <TextField name={meta.nome} label='Nome completo' maxLength={NOME_MAX_LENGTH} required />
        </Cell>

        <Cell size={12}>
          <TextField name={meta.nomeSocial} label='Nome social' maxLength={NOME_MAX_LENGTH} />
        </Cell>

        <Cell size={6}>
          <CpfField name={meta.cpf} label='CPF' disabled={naoPossuiCpf} required={!naoPossuiCpf} />
        </Cell>
        <Cell size={6} alignSelf='center'>
          <FormControl label={<span>&nbsp;</span>}>
            <CheckboxField name={meta.stNaoPossuiCpf} label='Cidadão não possui CPF' style={styles.checkboxInline} />
          </FormControl>
        </Cell>

        <Cell size={6}>
          <CnsField name={meta.cns} label='CNS' />
        </Cell>
        <Cell size={6}>
          <RecordStringSelectField
            name={meta.justificativaNaoPossuiCpf}
            record={motivoAusenciaCpfSinanRecord}
            label='Justificativa para não possuir CPF'
            disabled={!naoPossuiCpf}
            required={naoPossuiCpf}
          />
        </Cell>

        <Cell size={6}>
          <DateField name={meta.dataNascimento} label='Data de nascimento' maxDate={serverTime} required />
        </Cell>
        <Cell size={6}>
          <SexoSelectField name={meta.sexo} label='Sexo' required />
        </Cell>

        <Cell size={12}>
          <SimNaoRadioGroupField name={meta.estrangeiro} label='Estrangeiro?' required />
        </Cell>

        <Cell size={12}>
          <PaisSelectField name={meta.paisNascimento} label='País de nascimento' required />
        </Cell>

        <Cell size={12}>
          <MunicipioSelectField name={meta.municipioNascimento} label='Município de nascimento' required />
        </Cell>

        <Cell size={12}>
          <RecordStringSelectField name={meta.racaCor} record={racaCorSinanRecord} label='Raça/Cor' required />
        </Cell>

        <Cell size={4}>
          <SimNaoRadioGroupField name={meta.isCidadaoAldeado} label='Cidadão é aldeado?' required />
        </Cell>
        <Cell size={8}>
          <DseiSelectField
            name={meta.dsei}
            label='Nome do distrito sanitário especial (DSEI)'
            poloBaseId={value?.poloBase?.id}
            aldeiaId={value?.aldeia?.id}
            disabled={!isAldeado}
            required={isAldeado}
          />
        </Cell>

        <Cell size={12}>
          <EtniaSelectField name={meta.etnia} label='Etnia' disabled={!isIndigena} required={isIndigena} />
        </Cell>

        <Cell size={12}>
          <PoloBaseSelectField
            name={meta.poloBase}
            label='Nome do polo base'
            dseiId={value?.dsei?.id}
            aldeiaId={value?.aldeia?.id}
            disabled={!isAldeado}
            required={isAldeado}
          />
        </Cell>

        <Cell size={12}>
          <AldeiaSelectField
            name={meta.aldeia}
            label='Nome da aldeia'
            dseiId={value?.dsei?.id}
            poloBaseId={value?.poloBase?.id}
            disabled={!isAldeado}
            required={isAldeado}
          />
        </Cell>

        <Cell size={12}>
          <TextField
            name={meta.nomePai}
            label='Nome do pai'
            maxLength={NOME_MAX_LENGTH}
            disabled={desconheceNomePai}
            required={!desconheceNomePai}
          />
        </Cell>
        <Cell size={12}>
          <CheckboxField name={meta.desconheceNomePai} label='Desconhece essa informação' />
        </Cell>

        <Cell size={12}>
          <TextField
            name={meta.nomeMae}
            label='Nome da mãe'
            maxLength={NOME_MAX_LENGTH}
            disabled={desconheceNomeMae}
            required={!desconheceNomeMae}
          />
        </Cell>
        <Cell size={12}>
          <CheckboxField name={meta.desconheceNomeMae} label='Desconhece essa informação' />
        </Cell>

        <Cell size={12}>
          <RecordStringSelectField
            name={meta.identidadeGenero}
            record={identidadeGeneroSinanRecord}
            label='Identidade de gênero'
          />
        </Cell>

        <Cell size={12}>
          <RecordStringSelectField
            name={meta.orientacaoSexual}
            record={orientacaoSexualSinanRecord}
            label='Orientação sexual'
          />
        </Cell>

        <Cell size={6}>
          <SimNaoRadioGroupField
            name={meta.isMembroComunidadeTradicional}
            label='É membro de povo ou comunidade tradicional?'
            required
          />
        </Cell>
        <Cell size={6}>
          <RecordStringSelectField
            name={meta.povoComunidadeTradicional}
            record={povoComunidadeTradicionalRecord}
            label='Se sim, qual?'
            disabled={!isMembroComunidadeTradicional}
            required={isMembroComunidadeTradicional}
          />
        </Cell>

        <Cell size={12}>
          <RadioGroupField name={meta.gestante} label='É Gestante?' options={gestanteOptions} lines={2} required />
        </Cell>
      </Grid>
    </VFlow>
  )
}

const gestanteOptions = recordToSortedRadioOptions(gestanteSinanRecord)

const styles = {
  checkboxInline: css`
    margin-top: 0.25rem;
  `,
}
